import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import { useFormat } from '../context/FormatContext';
import UploadStep from './pipeline/UploadStep';
import AnalysisStep from './pipeline/AnalysisStep';
import StructureStep from './pipeline/StructureStep';
import PrintConfigStep from './pipeline/PrintConfigStep';
import ExportStep from './pipeline/ExportStep';
import PreviewStep from './pipeline/PreviewStep';

const {
  FiArrowLeft, FiUpload, FiActivity, FiLayout,
  FiDownload, FiChevronLeft, FiBook, FiEye
} = FiIcons;

const FormatProPipeline = () => {
  const { pipelineState, closeProject, goToPreviousStep, setPipelineState, activeProject } = useFormat();
  const { step } = pipelineState;

  const steps = [
    { num: 1, label: 'Upload', icon: FiUpload },
    { num: 2, label: 'Analyze', icon: FiActivity },
    { num: 3, label: 'Structure', icon: FiLayout },
    { num: 4, label: 'Print Layout', icon: FiBook },
    { num: 5, label: 'Export', icon: FiDownload },
    { num: 4.5, label: 'Preview', icon: FiEye },
  ];

  // Visible steps in the stepper (exclude 4.5 from the progress bar — it's between 4 and 5)
  const visibleSteps = [
    { num: 1, label: 'Upload', icon: FiUpload },
    { num: 2, label: 'Analyze', icon: FiActivity },
    { num: 3, label: 'Structure', icon: FiLayout },
    { num: 4, label: 'Print', icon: FiBook },
    { num: 5, label: 'Export', icon: FiDownload },
  ];

  // For stepper display: treat 4.5 as "between 4 and 5"
  const effectiveStep = step === 4.5 ? 4.5 : step;

  const isPreviewStep = step === 4.5;

  const handleSelectStep = (targetStepNum) => {
    if (step === targetStepNum) return;
    const hasManuscript = Boolean(
      (activeProject?.id && activeProject.id !== 'new') ||
      pipelineState.analysis ||
      pipelineState.file
    );
    if (targetStepNum > 1 && !hasManuscript) return;
    setPipelineState(prev => ({
      ...prev,
      step: targetStepNum,
      ...(targetStepNum < 5 ? { exportResult: null, exportType: null } : {})
    }));
  };

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4 mr-6">
            <button
              onClick={closeProject}
              className="flex items-center space-x-2 text-slate-500 hover:text-primary transition-colors text-xs font-bold uppercase tracking-widest"
            >
              <SafeIcon icon={FiArrowLeft} />
              <span>Dashboard</span>
            </button>
            {step > 1 && (
              <button
                onClick={goToPreviousStep}
                className="flex items-center space-x-2 text-slate-400 hover:text-secondary transition-colors text-xs font-bold uppercase tracking-widest pl-4 border-l border-slate-100"
              >
                <SafeIcon icon={FiChevronLeft} />
                <span>Previous Step</span>
              </button>
            )}
          </div>

          {/* Step Progress */}
          <div className="hidden lg:flex items-center space-x-6 mx-4">
            {visibleSteps.map((s, i) => {
              const isActive = step === s.num || (step === 4.5 && s.num === 5);
              const isDone = step > s.num && !(step === 4.5 && s.num === 5);
              const isPreviewActive = step === 4.5 && s.num === 5;
              const canClick = s.num === 1 || Boolean(
                (activeProject?.id && activeProject.id !== 'new') ||
                pipelineState.analysis ||
                pipelineState.file
              );

              return (
                <div key={s.num} className="flex items-center">
                  <button
                    type="button"
                    onClick={() => canClick && handleSelectStep(s.num)}
                    disabled={!canClick}
                    className={`flex items-center focus:outline-none transition-opacity ${
                      canClick ? 'cursor-pointer hover:opacity-80' : 'cursor-default'
                    }`}
                  >
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold transition-all ${
                      isPreviewActive
                        ? 'bg-amber-100 text-amber-600 ring-4 ring-amber-100'
                        : isActive
                          ? 'bg-primary text-white ring-4 ring-primary/20'
                          : isDone
                            ? 'bg-green-100 text-green-600'
                            : 'bg-slate-100 text-slate-400'
                    }`}>
                      {isDone && !isPreviewActive
                        ? <SafeIcon icon={FiIcons.FiCheck} />
                        : isPreviewActive
                          ? <SafeIcon icon={FiEye} />
                          : s.num}
                    </div>
                    <span className={`ml-2 text-xs font-bold uppercase tracking-widest ${
                      isActive || isPreviewActive ? 'text-slate-800' : 'text-slate-400'
                    }`}>
                      {isPreviewActive ? 'Preview' : s.label}
                    </span>
                  </button>
                  {i < visibleSteps.length - 1 && (
                    <div className="w-8 xl:w-10 h-[2px] bg-slate-100 ml-6" />
                  )}
                </div>
              );
            })}
          </div>

          {/* Mobile step indicator */}
          <div className="lg:hidden flex items-center space-x-2">
            {isPreviewStep ? (
              <span className="text-xs font-bold text-amber-600 uppercase tracking-widest">Preview</span>
            ) : (
              <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">
                Step {step} of 5
              </span>
            )}
          </div>

          <div className="text-primary font-serif font-bold hidden sm:block ml-6">FormatterPro Engine</div>
        </div>
      </header>

      <main className="flex-grow max-w-6xl w-full mx-auto px-6 py-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {step === 1 && <UploadStep />}
            {step === 2 && <AnalysisStep />}
            {step === 3 && <StructureStep />}
            {step === 4 && <PrintConfigStep />}
            {step === 4.5 && <PreviewStep />}
            {step === 5 && <ExportStep />}
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
};

export default FormatProPipeline;